import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ShareCard from "../components/ShareCard";
import { fetchForest, forestUrl, forestPath, forestLabel, isGroupForest, nextMilestone, CURRENT_YEAR } from "../forests";
import { forestBlocks, treesWord, PRICE_LINE } from "../shareCard";

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

function Forest() {
  const { id } = useParams();
  const [forest, setForest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    fetchForest(id)
      .then((f) => (f ? setForest(f) : setError("We couldn't find this forest.")))
      .catch((e) => {
        console.error("Forest:", e);
        setError("Couldn't load this forest. Please refresh.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="text-center text-gray-500 py-16">Loading forest…</p>;
  if (error)
    return (
      <div className="text-center py-16 px-4">
        <p className="text-gray-700 mb-4">{error}</p>
        <Link to="/forests" className="text-sritaru-700 underline">See all forests</Link>
      </div>
    );

  const group = isGroupForest(forest.name);
  const goal = nextMilestone(forest.trees);
  const pct = Math.min(100, (forest.trees / goal) * 100);

  return (
    <div className="py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/forests" className="text-sm text-sritaru-700 hover:underline">&larr; All forests</Link>

        <div className="bg-gradient-to-r from-sritaru-600 to-sritaru-700 text-white rounded-xl shadow-lg p-8 mt-3 mb-6 text-center">
          <p className="text-sritaru-200 text-sm uppercase tracking-wider">{group ? "Group forest" : "Team"}</p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-1 mb-4">{forestLabel(forest.name)} &#127795;</h1>
          <p className="text-6xl font-bold">{forest.trees.toLocaleString()}</p>
          <p className="text-sritaru-200 text-lg">{treesWord(forest.trees)} paid for or planted</p>
          <div className="max-w-sm mx-auto mt-5">
            <div className="h-3 bg-sritaru-800/60 rounded-full overflow-hidden">
              <div className="h-full bg-earth-200 rounded-full" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-sritaru-100 text-sm mt-2">
              {(goal - forest.trees).toLocaleString()} more to reach {goal.toLocaleString()}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-sritaru-100 mt-4">
            {forest.rank > 0 && forest.trees > 0 && <span>#{forest.rank} of {forest.of} {group ? "group forests" : "teams"}</span>}
            <span>{forest.treesThisYear.toLocaleString()} in {CURRENT_YEAR}</span>
            <span>{forest.donors} {forest.donors === 1 ? "dedication" : "dedications"}</span>
            {forest.pledged > 0 && <span>{forest.pledged.toLocaleString()} more pledged</span>}
          </div>
          <Link
            to={`/contribute?team=${forestPath(forest).split("/").pop()}`}
            className="inline-block bg-white text-sritaru-700 font-semibold px-6 py-3 rounded-lg mt-6 hover:bg-sritaru-50 transition-colors"
          >
            Plant a tree in this forest
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow p-4 sm:p-6 mb-6">
          <ShareCard
            title="📣 Share this forest with your group"
            blocks={forestBlocks({ forestName: forest.name, trees: forest.trees, donors: forest.donors })}
            ctaLead="Join our forest 🌱"
            shareText={
              forest.trees
                ? `Our ${forestLabel(forest.name)} has planted ${forest.trees.toLocaleString()} fruit ${treesWord(forest.trees)} with SriTaru Sankalpam, on farmers' land in AP & Telangana. ${PRICE_LINE}. Add yours:`
                : `Our ${forestLabel(forest.name)} is open on SriTaru Sankalpam: fruit trees on farmers' land in AP & Telangana. ${PRICE_LINE}. Plant the first one:`
            }
            shareUrl={forestUrl(forest)}
            fileName={`sritaru-forest-${forest.id}`}
          />
        </div>

        <div className="bg-white rounded-xl shadow p-4 sm:p-6">
          <h2 className="text-xl font-semibold text-sritaru-800 mb-4">Recent dedications</h2>
          {forest.recent.length === 0 ? (
            <p className="text-gray-600">No trees yet. Be the first to plant one in this forest.</p>
          ) : (
            <ul className="divide-y divide-sritaru-100">
              {forest.recent.map((c, i) => (
                <li key={i} className="py-3 flex gap-3 items-start">
                  <span className="text-2xl" aria-hidden="true">&#127793;</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-800">
                      <span className="font-semibold">{c.first_name}</span> ·{" "}
                      {c.num_trees} {treesWord(c.num_trees)}
                      {c.payment_status === "pledged" && <span className="text-xs text-earth-500"> (pledged)</span>}
                    </p>
                    {c.dedicated_to && <p className="text-sm text-gray-600 break-words">{c.dedicated_to}</p>}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(c.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default Forest;
